import React, { useEffect, useRef, useState } from "react";
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  Grid,
  Collapse,
  colors,
  Tooltip,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import fileDownload from "js-file-download";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { BsCheck2Circle } from "react-icons/bs";

import { api, copyObject } from "../../../services";
import { COLORS } from "../../../theme/colors";
import { ContextMenu } from "../../../components/contextMenu";
import trashIcon from "../../../assets/images/icons/trash.svg";
import infoIcon from "../../../assets/images/icons/info.svg";
import {
  Select,
  TextButton,
  TableTransparentTextInput,
  UploadButton,
} from "../../../components/base";
import { DEFAULT_ROW } from "../../aroundtown/PortfolioTable";
import { EXCEL_FILE_TYPE } from "../../../constants";
import { saveNotification } from "../../../redux/notification/actions";
import { USA } from "../../../components/portfolioTable/constants";
import { updateUSAState } from "../noSolar";

const useStyles = makeStyles((theme) => ({
  table: {
    maxHeight: "45vh",
    marginTop: theme.spacing(1.75),
  },
  row: {
    "&:hover": {
      "& td": {
        backgroundColor: `${colors.grey[100]} !important`,
      },
    },
    "& input": {
      cursor: "pointer",
    },
  },
  trashIcon: {
    position: "absolute",
    display: "none",
    cursor: "pointer",
    width: 14,
  },
  addRow: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  subHeadersRow: {
    position: "sticky",
    zIndex: 2,
    verticalAlign: "top",
  },
  subHeaderCell: {
    borderTop: "none",
  },
  headerRow: {
    verticalAlign: "top",
    "& th": {
      paddingTop: theme.spacing(1),
    },
  },
  invalidAddressRow: {
    "& td *": {
      color: `${COLORS.darks[0][50]} !important`,
    },
  },
  stickyCell: {
    position: "sticky",
    left: 0,
    zIndex: 1,
    borderRight: `1px solid ${COLORS.darks[0][10]}`,
    backgroundColor: COLORS.white,
  },
  downloadInvalidAddress: {
    color: COLORS.orange,
    cursor: "pointer",
    "&:hover": {
      color: COLORS.purple,
    },
  },
}));

const stickyCellHeaderStyle = {
  zIndex: 3,
  borderRight: `1px solid ${COLORS.darks[0][10]}`,
};

export const ColumnsData = ({ row, rowIndex, onCellDataChange, headers, mainKey, editable }) => {
  const classes = useStyles();

  return (
    <>
      {headers.map((header, index) => {
        if (header.subHeaders) {
          return (
            <ColumnsData
              key={index}
              row={row}
              rowIndex={rowIndex}
              headers={header.subHeaders}
              onCellDataChange={(index, subHeaderKey, v) =>
                onCellDataChange(index, header.key, v, subHeaderKey)
              }
              mainKey={header.key}
              editable={editable}
            />
          );
        }
        const value = mainKey ? row[mainKey]?.[header.key] : row[header.key];
        return (
          <TableCell key={index} className={header.fixed && classes.stickyCell}>
            {!editable ? (
              <Typography>{value}</Typography>
            ) : header.selectOptions ? (
              <Select
                options={header.selectOptions}
                value={(header.valueFunc ? header.valueFunc(value) : value) || ""}
                onChange={(v) => onCellDataChange(rowIndex, header.key, v)}
                displayEmpty={true}
                placeholder="Select"
                disabled={header.key === "state" && row.state_disabled}
              />
            ) : (
              <TableTransparentTextInput
                type={header.type}
                defaultValue={value}
                onBlur={(v) => onCellDataChange(rowIndex, header.key, v)}
              />
            )}
          </TableCell>
        );
      })}
    </>
  );
};

export const AdditionalLeftCells = ({
  showDeleteBtnCell,
  onDelete,
  showInvalidAddressInfoCell,
  isInvalidAddressCell,
  style,
}) => {
  return (
    <>
      {showDeleteBtnCell && (
        <TableCell style={style}>
          {onDelete && (
            <img
              onClick={onDelete}
              src={trashIcon}
              alt="trash-icon"
              style={{ paddingLeft: 2, paddingTop: 6, cursor: "pointer" }}
            />
          )}
        </TableCell>
      )}
      {showInvalidAddressInfoCell && (
        <TableCell style={style}>
          {isInvalidAddressCell && (
            <Tooltip
              placement="top"
              title="The address could not be verified by Google. Please check it one more time."
              arrow
            >
              <img src={infoIcon} alt="trash-icon" style={{ paddingLeft: 2, paddingTop: 6 }} />
            </Tooltip>
          )}
        </TableCell>
      )}
    </>
  );
};

export const BuildingsTable = ({
  rows,
  setRows,
  disableTable,
  deleteBuilding,
  headers,
  draftUrl,
}) => {
  const [rightClickEvent, setRightClickEvent] = useState(null);
  const [clickedRow, setClickedRow] = useState(null);
  const [copiedRow, setCopiedRow] = useState(null);
  const [sortColumns, setSortColumns] = useState({});
  const [mainHeaderHeight, setMainHeaderHeight] = useState(47);
  const [reuploading, setReuploading] = useState(false);

  const { clusters } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const mainHeaderRowRef = useRef();
  const newRowRef = useRef();
  const classes = useStyles();

  const validAddressCount = rows.filter((e) => e.is_valid_address).length;
  const inValidAddressCount = rows.length - validAddressCount;
  const hasSubHeaders = headers.some((e) => !!e.subHeaders);

  useEffect(() => {
    if (mainHeaderRowRef?.current?.clientHeight) {
      setMainHeaderHeight(mainHeaderRowRef.current.clientHeight);
    }
  }, [mainHeaderRowRef, hasSubHeaders]);

  useEffect(() => {
    if (!newRowRef.current) return;
    newRowRef.current.scrollIntoView();
  }, [newRowRef.current, rows.length]);

  const downloadInvalidAddress = () => {
    api
      .get(`${draftUrl}unverified-addresses/?clusters=${clusters[0].id}`, {
        responseType: "blob",
      })
      .then((res) => {
        fileDownload(res.data, "unverified_address_buildings.xlsx");
      });
  };

  const reuploadFile = async (event) => {
    const file = event.target.files[0];
    const form = new FormData();
    form.append("file", file);
    setReuploading(true);
    return await api
      .post(`${draftUrl}unverified-addresses/?clusters=${clusters[0].id}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data)
      .then((data) => {
        if (rows.length) setRows([]);
        setRows(updateUSAState(data));
      })
      .catch((err) => {
        const statusCode = err?.response?.status;
        let message;
        if (statusCode === 400) {
          if ("file" in err.response.data) {
            message = err.response.data["file"][0];
          } else if ("error" in err.response.data) {
            message = err.response.data["error"];
          }
        } else if (statusCode === 500) {
          message =
            "Error when processing the uploaded file. Make sure the uploaded file matched with template";
        } else {
          message = "Something went wrong. Please try again";
        }
        dispatch(saveNotification({ message, error: true }));
      })
      .finally(() => setReuploading(false));
  };

  const onCellDataChange = (rowIndex, key, value, subKey = null) => {
    if (typeof value === "string") {
      value = value.trimStart();
      if (!isNaN(value) && key !== "zip_code") {
        value = parseFloat(value);
      }
    }

    setRows((prev) => {
      const newRows = copyObject(prev);
      if (subKey) {
        newRows[rowIndex][key] = newRows[rowIndex][key] || {};
        newRows[rowIndex][key][subKey] = value;
      } else if (key === "country" && value === USA) {
        newRows[rowIndex]["state_disabled"] = false;
        newRows[rowIndex][key] = value;
      } else if (key === "country" && value !== USA) {
        newRows[rowIndex]["state_disabled"] = true;
        newRows[rowIndex]["state"] = "";
        newRows[rowIndex][key] = value;
      } else {
        newRows[rowIndex][key] = value;
      }
      return newRows;
    });
  };

  const addRowAbove = (rowIndex) => {
    const firstPart = rows.slice(0, rowIndex);
    firstPart.push(copyObject(DEFAULT_ROW));
    setRows(firstPart.concat(rows.slice(rowIndex)));
    if (copiedRow && copiedRow >= rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const addRowBelow = (rowIndex) => {
    const firstPart = rows.slice(0, rowIndex + 1);
    firstPart.push(copyObject(DEFAULT_ROW));
    setRows(firstPart.concat(rows.slice(rowIndex + 1)));
    if (copiedRow && copiedRow > rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const deleteRow = (rowIndex) => {
    setRows(rows.slice(0, rowIndex).concat(rows.slice(rowIndex + 1)));
    if (copiedRow > rowIndex) {
      setCopiedRow(copiedRow - 1);
    } else if (copiedRow === rowIndex) {
      setCopiedRow(null);
    }
  };

  const copyRow = (rowIndex) => {
    setCopiedRow(rowIndex);
  };

  const pasteRow = (rowIndex) => {
    if (copiedRow === null) return;
    const firstPart = rows.slice(0, rowIndex);
    firstPart.push(rows[copiedRow]);
    setRows(firstPart.concat(rows.slice(rowIndex)));
    if (copiedRow >= rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const getValueForSorting = (row, name, parentName = null) => {
    let v = parentName ? row[parentName]?.[name] : row[name];
    if (typeof v === "string") {
      v = v.toLowerCase();
    }
    return v;
  };

  const sortByColumn = (name, parentName = null) => {
    const columns = {};
    if (!sortColumns[name]) {
      columns[name] = "asc";
    } else if (sortColumns[name] === "asc") {
      columns[name] = "desc";
    } else {
      columns[name] = "asc";
    }
    setSortColumns(columns);
    setRows((r) => {
      const emptyRows = [];
      const rows = [];
      r.forEach((obj) => {
        if (!!getValueForSorting(obj, name, parentName)) {
          rows.push(obj);
        } else {
          emptyRows.push(obj);
        }
      });
      rows.sort((a, b) => {
        const aValue = getValueForSorting(a, name, parentName);
        const bValue = getValueForSorting(b, name, parentName);
        if (aValue > bValue) {
          return 1;
        } else if (aValue < bValue) {
          return -1;
        }
        return 0;
      });
      if (columns[name] === "asc") return rows.concat(emptyRows);
      return rows.reverse().concat(emptyRows);
    });
  };

  const addNewRow = () => {
    setRows((r) => {
      const newRows = copyObject(r);
      newRows.push(copyObject(DEFAULT_ROW));
      return newRows;
    });
  };

  const contextMenuItems = [
    { text: "Add row above", onClick: addRowAbove },
    { text: "Add row below", onClick: addRowBelow },
    { text: "Delete row", onClick: deleteRow },
    { text: "Copy", onClick: copyRow },
    { text: "Paste", onClick: pasteRow },
  ];

  return (
    <>
      <Collapse in={!!rows.length && !disableTable}>
        <Box
          mt={1}
          mb={3}
          py={2}
          px={3}
          style={{ backgroundColor: inValidAddressCount ? COLORS.lightOrange : COLORS.lightGreen }}
        >
          {inValidAddressCount ? (
            <>
              <Grid container alignItems="center" style={{ columnGap: 6 }}>
                <AiOutlineInfoCircle size={20} style={{ color: COLORS.orange }} />
                <Typography>
                  A total of {rows.length} {rows.length > 1 ? "buildings were" : "building was"}{" "}
                  successfully detected.
                </Typography>
                <Typography
                  className={classes.downloadInvalidAddress}
                  onClick={downloadInvalidAddress}
                >
                  There are {inValidAddressCount}{" "}
                  {inValidAddressCount > 1 ? "buildings" : "building"} with incorrect addresses.
                </Typography>
                <Typography>If possible, please fix and</Typography>
                <UploadButton
                  onlyText
                  accept={EXCEL_FILE_TYPE}
                  onChange={reuploadFile}
                  text="reupload a file"
                  textStyle={{ fontWeight: 400 }}
                  id="reload-unverified-address"
                  loading={reuploading}
                />
                <Typography>or</Typography>
                <Typography color="primary">correct in online table</Typography>
              </Grid>
            </>
          ) : (
            <Grid container alignItems="center" style={{ columnGap: 8 }}>
              <BsCheck2Circle size={20} style={{ color: COLORS.green, fontWeight: "bold" }} />
              <Typography>
                All {rows.length} {rows.length > 1 ? "buildings were" : "building was"} successfully
                detected. Please continue by clicking the “Confirm & Calculate” button
              </Typography>
            </Grid>
          )}
        </Box>
      </Collapse>

      <TableContainer className={classes.table}>
        <Table stickyHeader style={{ height: "100%" }}>
          <TableHead>
            <TableRow className={classes.headerRow} innerRef={mainHeaderRowRef}>
              <AdditionalLeftCells
                style={{ borderBottom: "none" }}
                showDeleteBtnCell={!disableTable}
                showInvalidAddressInfoCell={!!inValidAddressCount}
              />
              {headers.map((header, index) => (
                <TableCell
                  key={index}
                  style={{
                    height: "40px",
                    fontWeight: 600,
                    minWidth: header.minWidth,
                    ...(header.fixed && stickyCellHeaderStyle),
                    paddingBottom: !hasSubHeaders ? 30 : 0,
                    borderBottom: hasSubHeaders && !header.subHeaders && "none",
                  }}
                  colSpan={header.subHeaders?.length || 1}
                >
                  {header.subHeaders ? (
                    <Typography style={{ fontWeight: "bold" }}>{header.text}</Typography>
                  ) : (
                    <TableSortLabel
                      active={!!sortColumns[header.key]}
                      direction={sortColumns[header.key] || "asc"}
                      onClick={() => sortByColumn(header.key)}
                    >
                      <Typography style={{ fontWeight: "bold" }}>{header.text}</Typography>
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>

            {hasSubHeaders && (
              <TableRow className={classes.subHeadersRow} style={{ top: mainHeaderHeight }}>
                <AdditionalLeftCells
                  style={{ borderTop: "none" }}
                  showDeleteBtnCell={!disableTable}
                  showInvalidAddressInfoCell={!!inValidAddressCount}
                />
                {headers.map((header, j) => {
                  const subHeaders = header.subHeaders || [{}];
                  return (
                    <React.Fragment key={j}>
                      {subHeaders.map((subHeader, i) => (
                        <TableCell
                          className={classes.subHeaderCell}
                          key={i}
                          style={{
                            minWidth: subHeader.minWidth,
                            ...(header.fixed && stickyCellHeaderStyle),
                          }}
                        >
                          {subHeader.key && (
                            <TableSortLabel
                              active={!!sortColumns[subHeader.key]}
                              direction={sortColumns[subHeader.key] || "asc"}
                              onClick={() => sortByColumn(subHeader.key, header.key)}
                            >
                              <Typography style={{ fontWeight: "bold" }}>
                                {subHeader.text}
                              </Typography>
                            </TableSortLabel>
                          )}
                        </TableCell>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableRow>
            )}
          </TableHead>

          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                innerRef={index === rows.length - 1 && !row.id ? newRowRef : null}
                className={`${classes.row} ${!row.is_valid_address && classes.invalidAddressRow}`}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (!disableTable) {
                    setRightClickEvent(event);
                    setClickedRow(index);
                  }
                }}
                key={index}
              >
                <AdditionalLeftCells
                  showDeleteBtnCell={!disableTable}
                  showInvalidAddressInfoCell={!!inValidAddressCount}
                  onDelete={() => {
                    deleteRow(index);
                    if (row?.id) deleteBuilding(row.id);
                  }}
                  isInvalidAddressCell={row.id && !row.is_valid_address}
                />
                <ColumnsData
                  row={row}
                  rowIndex={index}
                  onCellDataChange={onCellDataChange}
                  headers={headers}
                  editable={!disableTable}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ContextMenu
        items={contextMenuItems}
        event={rightClickEvent}
        onClose={() => {
          setRightClickEvent(null);
          setClickedRow(null);
        }}
        clickedRow={clickedRow}
        copiedRow={copiedRow}
      />

      {!disableTable && (
        <Box className={classes.addRow}>
          <TextButton text="Add row" onClick={addNewRow} />
        </Box>
      )}
    </>
  );
};
